import { objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useRef, useState, useCallback, useMemo } from 'react';
import useBoolean from '../../hooks/use-boolean.esm.js';
import useCallbackRef from '../../hooks/use-callback-ref.esm.js';
import { useControllableProp } from '../../hooks/use-controllable.esm.js';
import useIsomorphicEffect from '../../hooks/use-isomorphic-effect.esm.js';
import { visuallyHiddenStyleForCheckboxOrRadio } from '../../themes/const.esm.js';
import { dataAttr } from '../../utils/dom.esm.js';
import { focus } from '../../utils/focus.esm.js';
import { callAllHandlers, scheduleMicrotask } from '../../utils/function.esm.js';
import { EMPTY_OBJECT } from '../../utils/object.esm.js';
import { mergeRefs } from '../../utils/react-helpers.esm.js';

var _excluded = ["forwardedRef", "defaultChecked", "isChecked", "isFocusable", "isDisabled", "isReadOnly", "isRequired", "onChange", "isIndeterminate", "isInvalid", "name", "value", "id", "onBlur", "onFocus", "aria-label", "aria-labelledby", "aria-invalid", "aria-describedby", "labelProps"];

// import { useWhatChanged } from '@simbathesailor/use-what-changed'

var visuallyHiddenStyle = {
  '&[type="checkbox"]': _objectSpread2({}, visuallyHiddenStyleForCheckboxOrRadio)
};
/**
 * useCheckbox that provides all the state and focus management logic
 * for a checkbox. It is consumed by the `Checkbox` component
 *
 */
function useCheckbox() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var forwardedRef = props.forwardedRef,
    defaultChecked = props.defaultChecked,
    checkedProp = props.isChecked,
    isFocusable = props.isFocusable,
    isDisabled = props.isDisabled,
    isReadOnly = props.isReadOnly,
    isRequired = props.isRequired,
    onChange = props.onChange,
    isIndeterminate = props.isIndeterminate,
    isInvalid = props.isInvalid,
    name = props.name,
    value = props.value,
    id = props.id,
    onBlur = props.onBlur,
    onFocus = props.onFocus,
    ariaLabel = props['aria-label'],
    ariaLabelledBy = props['aria-labelledby'],
    ariaInvalid = props['aria-invalid'],
    ariaDescribedBy = props['aria-describedby'],
    _props$labelProps = props.labelProps,
    _labelProps = _props$labelProps === void 0 ? EMPTY_OBJECT : _props$labelProps,
    htmlProps = _objectWithoutProperties(props, _excluded);
  var onChangeProp = useCallbackRef(onChange);
  var onBlurProp = useCallbackRef(onBlur);
  var onFocusProp = useCallbackRef(onFocus);
  var _useBoolean = useBoolean(),
    _useBoolean2 = _slicedToArray(_useBoolean, 2),
    isFocused = _useBoolean2[0],
    setFocused = _useBoolean2[1];
  var _useBoolean3 = useBoolean(),
    _useBoolean4 = _slicedToArray(_useBoolean3, 2),
    isHovered = _useBoolean4[0],
    setHovered = _useBoolean4[1];
  var _useBoolean5 = useBoolean(),
    _useBoolean6 = _slicedToArray(_useBoolean5, 2),
    isActive = _useBoolean6[0],
    setActive = _useBoolean6[1];
  var inputRef = useRef(null);
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    rootIsLabelElement = _useState2[0],
    setRootIsLabelElement = _useState2[1];
  var _useState3 = useState(!!defaultChecked),
    _useState4 = _slicedToArray(_useState3, 2),
    checkedState = _useState4[0],
    setCheckedState = _useState4[1];
  var _useControllableProp = useControllableProp(checkedProp, checkedState),
    _useControllableProp2 = _slicedToArray(_useControllableProp, 2),
    isControlled = _useControllableProp2[0],
    isChecked = _useControllableProp2[1];
  var handleChange = useCallback(function (event) {
    if (isReadOnly || isDisabled) {
      event.preventDefault();
      return;
    }
    if (!isControlled) {
      if (isChecked) {
        setCheckedState(event.target.checked);
      } else {
        setCheckedState(isIndeterminate ? true : event.target.checked);
      }
    }
    onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(event);
  }, [isReadOnly, isDisabled, isChecked, isControlled, isIndeterminate, onChangeProp]);
  useIsomorphicEffect(function () {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(isIndeterminate);
    }
  }, [isIndeterminate]);
  var trulyDisabled = isDisabled && !isFocusable;
  var onKeyDown = useCallback(function (event) {
    if (event.key === ' ') {
      setActive.on();
    }
  }, [setActive]);
  var onKeyUp = useCallback(function (event) {
    if (event.key === ' ') {
      setActive.off();
    }
  }, [setActive]);

  /**
   * Sync state with uncontrolled form libraries like `react-hook-form`.
   *
   * These libraries set the checked value for input fields
   * using their refs. For the checkbox, it sets `ref.current.checked = true | false` directly.
   *
   * This means the `isChecked` state will get out of sync with `ref.current.checked`,
   * even though the input validation with work, the UI will not be up to date.
   *
   * Let's correct that by checking and syncing the state accordingly.
   */
  useIsomorphicEffect(function () {
    if (!inputRef.current) return;
    var notInSync = inputRef.current.checked !== isChecked;
    if (notInSync) {
      setCheckedState(inputRef.current.checked);
    }
  }, [inputRef.current]);
  var checkboxProps = useMemo(function () {
    var onPressDown = function onPressDown(event) {
      // On mousedown, the input blurs and returns focus to the `body`,
      // we need to prevent this. Native checkboxes keeps focus on `input`
      event.preventDefault();
      setActive.on();
    };
    return {
      'data-active': dataAttr(isActive),
      'data-hover': dataAttr(isHovered),
      'data-checked': dataAttr(isChecked),
      'data-focus': dataAttr(isFocused),
      'data-indeterminate': dataAttr(isIndeterminate),
      'data-disabled': dataAttr(isDisabled),
      'data-invalid': dataAttr(isInvalid),
      'data-readonly': dataAttr(isReadOnly),
      'aria-hidden': true,
      onMouseDown: onPressDown,
      onMouseUp: setActive.off,
      onMouseEnter: setHovered.on,
      onMouseLeave: setHovered.off
    };
  }, [isActive, isChecked, isDisabled, isFocused, isHovered, isIndeterminate, isInvalid, isReadOnly, setActive, setHovered.off, setHovered.on]);
  var rootProps = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, htmlProps), {}, {
      ref: function ref(node) {
        if (!node) return;
        setRootIsLabelElement(node.tagName === 'LABEL');
      },
      onClick: callAllHandlers(htmlProps.onClick, function () {
        /**
         * Accessibility:
         *
         * Ideally, `getRootProps` should be spread unto a `label` element.
         *
         * If the element was changed using the `as` prop or changing
         * the dom node `getRootProps` is spread unto (to a `div` or `span`), we'll trigger
         * click on the input when the element is clicked.
         * @see Issue https://github.com/chakra-ui/chakra-ui/issues/3480
         */
        if (!rootIsLabelElement) {
          var _inputRef$current;
          (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.click();
          focus(inputRef.current, {
            nextTick: true
          });
        }
      }),
      'data-disabled': dataAttr(isDisabled)
    });
  }, [htmlProps, isDisabled, rootIsLabelElement]);

  // useWhatChanged(
  //   [
  //     ariaDescribedBy,
  //     ariaInvalid,
  //     ariaLabel,
  //     ariaLabelledBy,
  //     forwardedRef,
  //     handleChange,
  //     id,
  //     isChecked,
  //     isDisabled,
  //     isIndeterminate,
  //     isInvalid,
  //     isReadOnly,
  //     isRequired,
  //     name,
  //     onBlurProp,
  //     onFocusProp,
  //     onKeyDown,
  //     onKeyUp,
  //     setFocused.off,
  //     setFocused.on,
  //     trulyDisabled,
  //     value
  //   ],
  //   'ariaDescribedBy, ariaInvalid, ariaLabel, ariaLabelledBy, forwardedRef, handleChange, id, isChecked, isDisabled, isIndeterminate, isInvalid, isReadOnly, isRequired, name, onBlurProp, onFocusProp, onKeyDown, onKeyUp, setFocused.off, setFocused.on, trulyDisabled, value'
  // )

  var inputProps = useMemo(function () {
    var onFocus = function onFocus() {
      scheduleMicrotask(setFocused.on);
    };
    return {
      ref: mergeRefs(inputRef, forwardedRef),
      type: 'checkbox',
      name: name,
      value: value,
      id: id,
      onChange: handleChange,
      onBlur: callAllHandlers(onBlurProp, setFocused.off),
      onFocus: callAllHandlers(onFocusProp, onFocus),
      onKeyDown: onKeyDown,
      onKeyUp: onKeyUp,
      required: isRequired,
      checked: isChecked,
      indeterminate: isIndeterminate,
      disabled: trulyDisabled,
      readOnly: isReadOnly,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-invalid': ariaInvalid ? Boolean(ariaInvalid) : isInvalid,
      'aria-describedby': ariaDescribedBy,
      'aria-disabled': isDisabled,
      __css: visuallyHiddenStyle
    };
  }, [ariaDescribedBy, ariaInvalid, ariaLabel, ariaLabelledBy, forwardedRef, handleChange, id, isChecked, isDisabled, isIndeterminate, isInvalid, isReadOnly, isRequired, name, onBlurProp, onFocusProp, onKeyDown, onKeyUp, setFocused.off, setFocused.on, trulyDisabled, value]);
  var labelProps = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, _labelProps), {}, {
      onMouseDown: callAllHandlers(_labelProps.onMouseDown, stopEvent),
      onTouchStart: callAllHandlers(_labelProps.onTouchStart, stopEvent),
      'data-disabled': dataAttr(isDisabled),
      'data-checked': dataAttr(isChecked),
      'data-invalid': dataAttr(isInvalid)
    });
  }, [_labelProps, isChecked, isDisabled, isInvalid]);
  var state = {
    isInvalid: isInvalid,
    isFocused: isFocused,
    isChecked: isChecked,
    isActive: isActive,
    isHovered: isHovered,
    isIndeterminate: isIndeterminate,
    isDisabled: isDisabled,
    isReadOnly: isReadOnly,
    isRequired: isRequired
  };
  return {
    state: state,
    rootProps: rootProps,
    checkboxProps: checkboxProps,
    inputProps: inputProps,
    labelProps: labelProps,
    htmlProps: htmlProps
  };
}

/**
 * Prevent `onBlur` being fired when the checkbox label is touched
 */
function stopEvent(event) {
  event.preventDefault();
  event.stopPropagation();
}

export { useCheckbox };
//# sourceMappingURL=use-checkbox.esm.js.map
